import { z } from 'zod';
import { EnvironmentLocales } from './localization.ts';
import { ApprovalPolicy } from './approvals.ts';
import { EXTRAS_CHANNELS } from './extras.ts';
import { LegacyRemovalEvent } from './card-delivery.ts';
import { ACTIVITY_LOG_RETENTION_DAYS } from './activity-log.ts';

export const EnvironmentKind = z.enum(['dev', 'qa', 'staging', 'prod', 'custom']);
export const AppPlatform = z.enum(['android', 'ios', 'web']);
export const EnvironmentKey = z.string().regex(/^[a-z][a-z0-9-]{1,31}$/, 'lowercase letters, digits and dashes, 2-32 chars');
const IanaTimezone = z.string().refine((tz) => {
  try {
    new Intl.DateTimeFormat('en', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}, 'unknown IANA time zone');

export const EnvironmentSettings = z
  .object({
    testOnly: z.boolean().optional(),
    allowedRecipientsGroupId: z.uuid().nullable().optional(),
    maxRecipients: z.number().int().positive().nullable().optional(),
    /** Longest window of segment filters over raw events (rolling counts, event properties), BB-42; default 90. */
    eventPropertyLookbackDays: z.number().int().min(1).max(730).optional(),
    /** An active campaign without sends for this many days is listed as idle (BB-96); default 30. */
    campaignIdleDays: z.number().int().min(1).max(365).optional(),
    /** Channels whose extras schema refuses unknown keys (BB-58); otherwise they are warnings. */
    extrasStrict: z.partialRecord(z.enum(EXTRAS_CHANNELS), z.boolean()).optional(),
    /** Approval workflow (BB-106); unset: on in production environments, off elsewhere. */
    approvals: ApprovalPolicy.optional(),
    /** Locales of messages (BB-55); unset — only `defaultLocale`. */
    locales: EnvironmentLocales.optional(),
    /** Where a profile's language comes from: the standard `language` or a custom attribute key (BB-55). */
    localeAttribute: z.string().min(1).max(255).optional(),
    /** Migration rule: `Content Card Removal {campaign}` withdraws cards by extras (BB-65); `null` or unset — off. */
    legacyRemovalEvent: LegacyRemovalEvent.nullable().optional(),
    /** How many days the Message Activity Log shows (BB-105); default 14. */
    activityLogRetentionDays: z.number().int().min(ACTIVITY_LOG_RETENTION_DAYS.min).max(ACTIVITY_LOG_RETENTION_DAYS.max).optional(),
    /** Profile updates of Canvas User Update steps per minute (BB-120); default 200 000. */
    canvasUserUpdatesPerMinute: z.number().int().min(1).max(10_000_000).optional(),
  })
  .loose();

export const CreateEnvironmentBody = z.object({
  key: EnvironmentKey,
  name: z.string().trim().min(1).max(80),
  kind: EnvironmentKind,
  defaultTimezone: IanaTimezone.default('UTC'),
  defaultLocale: z.string().min(2).max(35).default('en'),
});

export const UpdateEnvironmentBody = z
  .object({
    name: z.string().trim().min(1).max(80).optional(),
    defaultTimezone: IanaTimezone.optional(),
    defaultLocale: z.string().min(2).max(35).optional(),
    settings: EnvironmentSettings.optional(),
  })
  .strict();

export const CreateAppBody = z.object({
  name: z.string().trim().min(1).max(80),
  platform: AppPlatform,
  identifier: z.string().trim().min(1).max(255),
});

export const SdkVersion = z.string().regex(/^\d+(\.\d+){0,3}(-[0-9A-Za-z.]+)?$/, 'dotted version such as 1.4.0');

/**
 * Silent-push uninstall tracking of an app (BB-158): a probe at most once per `probeIntervalDays` per installation
 * without a session for 24 hours. Never more often than weekly (APNs throttles background pushes).
 */
export const UNINSTALL_PROBE_INTERVAL_DAYS = { min: 7, max: 30, default: 7 } as const;
export const UninstallTrackingSettings = z.strictObject({
  enabled: z.boolean().optional(),
  probeIntervalDays: z.int().min(UNINSTALL_PROBE_INTERVAL_DAYS.min).max(UNINSTALL_PROBE_INTERVAL_DAYS.max).optional(),
});

export const UpdateAppBody = z
  .strictObject({
    name: z.string().trim().min(1).max(80).optional(),
    /** The same switch as `uninstallTracking.enabled` (BB-13 form of the setting, kept for existing clients). */
    uninstallTrackingEnabled: z.boolean().optional(),
    uninstallTracking: UninstallTrackingSettings.optional(),
    minSupportedSdkVersion: SdkVersion.nullable().optional(),
  })
  .refine((b) => b.uninstallTrackingEnabled === undefined || b.uninstallTracking?.enabled === undefined || b.uninstallTrackingEnabled === b.uninstallTracking.enabled, {
    path: ['uninstallTracking', 'enabled'],
    message: 'contradicts uninstallTrackingEnabled',
  });

export const RotateSdkKeyBody = z.strictObject({
  /** How long the old key keeps working: app builds with it stay in users' hands (default 14 days). */
  retiringDays: z.number().int().min(0).max(90).default(14),
});

export const IdParam = z.object({ id: z.uuid() });

export type CreateEnvironmentInput = z.infer<typeof CreateEnvironmentBody>;
export type UpdateEnvironmentInput = z.infer<typeof UpdateEnvironmentBody>;
export type CreateAppInput = z.infer<typeof CreateAppBody>;
export type UpdateAppInput = z.infer<typeof UpdateAppBody>;
